import type { IEvaultApi } from './use-evault-api';
import type {
	IPaginated,
	IVaultActivityRow,
	IVaultCertificate,
	IVaultEvidence,
} from './types';

import { useCallback, useEffect, useRef, useState } from 'react';

import { EVAULT_API, toQuery } from './use-evault-api';

export interface IAsyncState<T> {
	isLoading: boolean;
	isLoaded: boolean;
	/** The server's own sentence, or '' when the last load succeeded. */
	error: string;
	/**
	 * The HTTP status behind `error`, or null when no answer came back at all. The
	 * drawer's error band reads this to say the right thing: a 404 or 403 is the
	 * server refusing, and printing "the service did not respond" over a refusal
	 * sent the reader to check their connection for a permission problem.
	 */
	errorStatus: number | null;
	data: T | null;
}

const IDLE = {
	isLoading: false,
	isLoaded: false,
	error: '',
	errorStatus: null,
	data: null,
};

/**
 * The two requests behind the document drawer.
 *
 * NO `GET /vault-documents/:id`. The drawer is opened from a row the deck
 * already holds, and that row carries every key the single-document endpoint
 * returns — verified against `vault-documents/index.ts` on 2026-09-04 and
 * recorded in spec 25 §4. Re-fetching it would add a request, a spinner and a
 * second source of truth for the Details section, and buy nothing. The one
 * thing the row genuinely does not carry is custody, and that is not on that
 * endpoint either: it comes from the evidence package below.
 *
 * WHY THE EVIDENCE FETCH IS LAZY, AND THE AUDIT FETCH IS NOT.
 * `GET /vault-evidence/:id` downloads the stored original and re-hashes it
 * (`recomputeHash`), so it is the most expensive read in the feature and it is
 * paid on the SERVER's storage bill, not just in latency. Opening a drawer to
 * read a file size must not trigger it — §7.4 is explicit that it loads on
 * section expand. `GET /vault-audit` is a paginated index read, so Activity
 * loads with the drawer and is simply there.
 *
 * Custody and Integrity share the one evidence call (§7.3): expanding either
 * loads it, expanding the second finds it already loaded.
 */
export const useDocumentDrawer = (api: IEvaultApi, documentId: string | null) => {
	const { get, post, vaultToken } = api;

	const [evidence, setEvidence] = useState<IAsyncState<IVaultEvidence>>(IDLE);
	const [audit, setAudit] = useState<IAsyncState<IVaultActivityRow[]>>(IDLE);
	const [certificate, setCertificate] = useState<IAsyncState<IVaultCertificate>>(IDLE);

	/* One issue at a time. The button is disabled while `isLoading`, but a double
	   click lands both handlers before React repaints, and the second would render a
	   second PDF in Chromium and burn a second ordinal on identical facts. */
	const issuing = useRef(false);

	/* Which document the state in hand describes. A drawer re-opened on another
	   row must not show the previous document's hashes for the instant before
	   the new ones land — worse here than anywhere else in the deck, because the
	   values are evidence about a specific file. */
	const shown = useRef<string | null>(null);

	/* Stale-response guards, one per request: two rapid opens issue two of each,
	   and the slower must not paint over the newer. */
	const evidenceTicket = useRef(0);
	const auditTicket = useRef(0);

	/* `ensureEvidence` cannot guard on state alone. Expanding Custody and
	   Integrity in the same tick — which a double click does, and which the
	   drawer's own "open everything" path did in testing — reads the SAME
	   pre-update `isLoading: false` twice and fires the expensive call twice. A
	   ref is written synchronously, so the second caller sees the first. */
	const evidenceAsked = useRef(false);

	const loadAudit = useCallback(async () => {
		if (!vaultToken || !documentId) return;
		const mine = (auditTicket.current += 1);
		setAudit(prev => ({ ...prev, isLoading: true, error: '' }));

		/* `view=activity`: signing history + custody trail, with names and a
		   sentence per row, so the drawer reads like eSign's Packet History. */
		const result = await get<IPaginated<IVaultActivityRow>>(
			`${EVAULT_API.VAULT_AUDIT}${toQuery({
				targetId: documentId,
				view: 'activity',
			})}`
		);
		if (mine !== auditTicket.current) return;

		setAudit(
			result.kind === 'ok'
				? {
						isLoading: false,
						isLoaded: true,
						error: '',
						errorStatus: null,
						data: result.data?.items ?? [],
				  }
				: {
						isLoading: false,
						isLoaded: true,
						error: result.message,
						errorStatus: result.status ?? null,
						data: null,
				  }
		);
	}, [get, documentId, vaultToken]);

	const loadEvidence = useCallback(async () => {
		if (!vaultToken || !documentId) return;
		evidenceAsked.current = true;
		const mine = (evidenceTicket.current += 1);
		setEvidence(prev => ({ ...prev, isLoading: true, error: '' }));

		const result = await get<IVaultEvidence>(
			`${EVAULT_API.VAULT_EVIDENCE}/${documentId}`
		);
		if (mine !== evidenceTicket.current) return;

		setEvidence(
			result.kind === 'ok'
				? {
						isLoading: false,
						isLoaded: true,
						error: '',
						errorStatus: null,
						data: result.data,
				  }
				: {
						isLoading: false,
						isLoaded: true,
						error: result.message,
						errorStatus: result.status ?? null,
						data: null,
				  }
		);
	}, [get, documentId, vaultToken]);

	/**
	 * Called on every expand of Custody or Integrity; issues the request once.
	 *
	 * `isLoaded` rather than `data` is the guard, so a document whose evidence
	 * failed to load does not re-request on every toggle — the section shows its
	 * own retry, and that retry is `loadEvidence` directly.
	 */
	const ensureEvidence = useCallback(() => {
		if (evidenceAsked.current || evidence.isLoading || evidence.isLoaded) return;
		void loadEvidence();
	}, [evidence.isLoading, evidence.isLoaded, loadEvidence]);

	/**
	 * Issue (or reuse) the certificate of custody and hand back a download URL.
	 *
	 * `POST` rather than a `GET` of the latest, because the server's answer depends on
	 * whether custody has moved: it returns the existing certificate when the ownership
	 * state is unchanged and mints a new one when it is not. Asking the client to work
	 * that out would mean duplicating the rule in two places and getting a stale
	 * certificate for a transferred original when they disagreed.
	 *
	 * The PDF is NOT fetched here. `downloadUrl` is a short-lived signed link the browser
	 * follows directly, so a 300 KB document never passes through this hook's state.
	 */
	const issueCertificate = useCallback(async (reissue = false): Promise<IVaultCertificate | null> => {
		if (!vaultToken || !documentId || issuing.current) return null;
		issuing.current = true;
		setCertificate(prev => ({ ...prev, isLoading: true, error: '' }));

		/* `reissue` asks the server for a fresh certificate (ordinal N+1) even though one
		   exists for this ownership: everything is re-measured, so it carries today's
		   lock, package and attestation. The earlier one is kept. */
		const result = await post<IVaultCertificate>(EVAULT_API.VAULT_CERTIFICATES, {
			documentId,
			...(reissue ? { reissue: true } : {}),
		});
		issuing.current = false;

		if (result.kind !== 'ok') {
			setCertificate({
				isLoading: false,
				isLoaded: true,
				error: result.message,
				errorStatus: result.status ?? null,
				data: null,
			});
			return null;
		}

		setCertificate({
			isLoading: false,
			isLoaded: true,
			error: '',
			errorStatus: null,
			data: result.data,
		});
		return result.data;
	}, [post, documentId, vaultToken]);

	/* A new document resets all three, then fetches only the cheap one. */
	useEffect(() => {
		if (shown.current === documentId) return;
		shown.current = documentId;
		evidenceAsked.current = false;
		issuing.current = false;
		setEvidence(IDLE);
		setAudit(IDLE);
		setCertificate(IDLE);
		if (documentId) void loadAudit();
	}, [documentId, loadAudit]);

	return {
		evidence,
		audit,
		certificate,
		loadEvidence,
		ensureEvidence,
		issueCertificate,
		refetchAudit: loadAudit,
	};
};
